"use client";

/**
 * useEoaSweep — shared logic for moving USDC/pUSD stranded on the Privy
 * login EOA to the user's deposit (trading) wallet.
 *
 * Wraps useEoaBalance (detection) + the sign/airdrop flow so both the
 * floating EoaSweepBanner and the inline card in /settings?tab=wallet
 * drive the exact same on-chain sequence:
 *
 *   1. Backend airdrops ~0.05 POL to the EOA (sponsor-paid). Idempotent —
 *      skipped if the EOA already has ≥0.005 POL.
 *   2. Poll /eoa-balance until POL lands.
 *   3. For each token with a balance, sign an ERC20 `transfer` from the
 *      EOA to the deposit wallet via Privy's embedded wallet.
 *
 * Funds can't be lost: destination is the user's own deposit wallet
 * (deterministic CREATE2 of EOA + factory, persisted in DB, verified
 * pre-sign by the backend). On any failure, balances stay safe on the EOA.
 */

import { useCallback, useEffect, useState } from "react";
import { ethers } from "ethers";
import { useSendTransaction } from "@privy-io/react-auth";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import { useEoaBalance } from "./useEoaBalance";
import { useAuth } from "./useAuth";

// Polygon token addresses
const USDC_E_ADDRESS = "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174";
const USDC_NATIVE_ADDRESS = "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359";
const PUSD_ADDRESS = "0xC011a7E12a19f7B1f670d46F03B03f3342E82DFB";
const POLYGON_CHAIN_ID = 137;
const TRANSFER_SELECTOR = "0xa9059cbb";

interface TokenSweep {
  symbol: "USDC.e" | "USDC" | "pUSD";
  address: string;
  balance: number;
}

function encodeTransfer(to: string, amountWei: ethers.BigNumber): `0x${string}` {
  const padTo = to.toLowerCase().replace(/^0x/, "").padStart(64, "0");
  const padAmount = amountWei.toHexString().slice(2).padStart(64, "0");
  return `${TRANSFER_SELECTOR}${padTo}${padAmount}` as `0x${string}`;
}

async function waitForPol(opts: { minPol: number; timeoutMs: number; intervalMs: number }) {
  const { minPol, timeoutMs, intervalMs } = opts;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const res = await authFetch("/api/wallet/eoa-balance").catch(() => null);
    if (res && res.ok) {
      const data = await res.json();
      const pol: number = data?.balances?.pol ?? 0;
      if (pol >= minPol) return true;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return false;
}

export interface EoaSweepState {
  eoa: string | null;
  depositWallet: string | null;
  balances: ReturnType<typeof useEoaBalance>["balances"];
  needsMove: boolean;
  needsGas: boolean;
  loading: boolean;
  running: boolean;
  /** True once a sweep moved every detected token in this session.
   *  Lets the banner / inline card hide IMMEDIATELY on success instead
   *  of waiting for the on-chain balance + 15s backend cache to catch
   *  up — otherwise the notice lingers after the funds are gone and the
   *  user re-clicks (predictfren confusion, 2026-05-28). */
  swept: boolean;
  /** Run the full airdrop-gas → sign-transfers sweep. No-op while running. */
  move: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useEoaSweep(): EoaSweepState {
  const { eoa, depositWallet, balances, needsMove, needsGas, loading, refetch } = useEoaBalance();
  const { currentWalletAddress } = useAuth();
  const { sendTransaction: privySendTransaction } = useSendTransaction();
  const [running, setRunning] = useState(false);
  const [swept, setSwept] = useState(false);

  // Reset the optimistic flag once the backend agrees there's nothing to
  // move (needsMove false). If the user later funds the EOA again,
  // needsMove flips back to true with swept already false → the notice
  // reappears as expected.
  useEffect(() => {
    if (!needsMove) setSwept(false);
  }, [needsMove]);

  const move = useCallback(async () => {
    if (!eoa || !depositWallet || !balances) return;
    if (running) return;
    setRunning(true);

    try {
      // Defense in depth: the embedded wallet active right now MUST match
      // the EOA we're about to send from. If Privy attached a different
      // wallet (cross-app migration, etc.), abort.
      if (currentWalletAddress && currentWalletAddress.toLowerCase() !== eoa.toLowerCase()) {
        toast.error("Wallet mismatch — refresh and try again");
        return;
      }

      // Read a LIVE balance right before building the transfers. The
      // `balances` in React state can be up to ~60s stale (poll interval),
      // and acting on it built an ERC20 transfer for funds that had
      // already moved → "transfer amount exceeds balance" revert + an
      // uncaught estimateGas rejection from Privy (predictfren,
      // 2026-05-28). `?fresh=1` bypasses the 15s backend cache so the
      // amount we sign matches what's actually on-chain.
      let liveBalances = balances;
      let liveNeedsGas = needsGas;
      try {
        const freshRes = await authFetch("/api/wallet/eoa-balance?fresh=1");
        if (freshRes.ok) {
          const fresh = await freshRes.json();
          if (fresh?.balances) liveBalances = fresh.balances;
          if (typeof fresh?.needsGas === "boolean") liveNeedsGas = fresh.needsGas;
        }
      } catch {
        // Fall back to React-state balances if the fresh read fails —
        // worst case is the old behaviour, not a regression.
      }

      const sweeps: TokenSweep[] = [];
      if (liveBalances.usdce > 0) sweeps.push({ symbol: "USDC.e", address: USDC_E_ADDRESS, balance: liveBalances.usdce });
      if (liveBalances.usdc > 0) sweeps.push({ symbol: "USDC", address: USDC_NATIVE_ADDRESS, balance: liveBalances.usdc });
      if (liveBalances.pusd > 0) sweeps.push({ symbol: "pUSD", address: PUSD_ADDRESS, balance: liveBalances.pusd });

      if (sweeps.length === 0) {
        // Funds already moved (or never landed). Refresh so the banner /
        // inline card hides instead of leaving a dead button.
        toast.message("Nothing to move — your funds are already in the trading wallet");
        await refetch();
        return;
      }

      // Step 1: airdrop POL if needed
      if (liveNeedsGas) {
        toast.loading("Funding gas...", { id: "eoa-sweep" });
        const airdropRes = await authFetch("/api/wallet/eoa-sweep/airdrop-gas", { method: "POST" });
        if (!airdropRes.ok) {
          const err = await airdropRes.json().catch(() => ({}));
          throw new Error(err?.error || "Failed to fund gas");
        }
        const airdropData = await airdropRes.json();
        if (!airdropData?.skipped) {
          toast.loading("Waiting for gas to confirm...", { id: "eoa-sweep" });
          const landed = await waitForPol({ minPol: 0.005, timeoutMs: 60_000, intervalMs: 3_000 });
          if (!landed) throw new Error("Gas didn't arrive in time. Try again in 30s.");
        }
      }

      // Step 2: sign + send one ERC20 transfer per token
      const txHashes: string[] = [];
      for (let i = 0; i < sweeps.length; i++) {
        const tok = sweeps[i];
        toast.loading(
          sweeps.length > 1
            ? `Moving ${tok.symbol} (${i + 1}/${sweeps.length}) — sign in wallet`
            : `Moving ${tok.symbol} — sign in wallet`,
          { id: "eoa-sweep" }
        );

        const amountWei = ethers.utils.parseUnits(tok.balance.toFixed(6), 6);
        const data = encodeTransfer(depositWallet, amountWei);
        try {
          const result = await privySendTransaction(
            { to: tok.address as `0x${string}`, data, chainId: POLYGON_CHAIN_ID },
            { address: eoa as `0x${string}` }
          );
          txHashes.push(result.hash);
        } catch (e: any) {
          // pUSD may revert due to Polymarket's allow-list. Surface a
          // specific message so support can route the user to a manual sweep.
          if (tok.symbol === "pUSD") {
            toast.error("pUSD transfer rejected by Polymarket allow-list. Contact support to move pUSD manually.", { id: "eoa-sweep" });
            continue;
          }
          throw e;
        }
      }

      const allMoved = txHashes.length === sweeps.length;
      toast.success(
        allMoved
          ? `Funds moved to deposit wallet ✓`
          : `Moved ${txHashes.length} of ${sweeps.length} tokens. Some may need manual support.`,
        { id: "eoa-sweep" }
      );

      // Optimistically hide the notice the instant every token cleared —
      // the balance read lags the tx by a few seconds (mine time + 15s
      // backend cache), and leaving the button up invites a confused
      // re-click on already-moved funds.
      if (allMoved) setSwept(true);

      await refetch();
    } catch (e: any) {
      toast.error(e?.message || "Failed to move funds", { id: "eoa-sweep" });
    } finally {
      setRunning(false);
    }
  }, [eoa, depositWallet, balances, needsGas, currentWalletAddress, privySendTransaction, refetch, running]);

  return { eoa, depositWallet, balances, needsMove, needsGas, loading, running, swept, move, refetch };
}
